import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  appleClientId,
  appleRedirectUri,
  describeAppleError,
  exchangeAppleIdentityToken,
  fetchAuthMe,
  getStoredSessionToken,
  isAppleClientConfigured,
  loadAppleScript,
  setStoredSessionToken,
  type CmsUser,
} from "./lib/appleAuth";

type AuthState = {
  loading: boolean;
  configured: boolean;
  sessionToken: string | null;
  user: CmsUser | null;
  isAdmin: boolean;
  deniedMessage: string | null;
  signInWithApple: () => Promise<void>;
  completeAppleRedirect: (identityToken: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [user, setUser] = useState<CmsUser | null>(null);
  const [deniedMessage, setDeniedMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const token = getStoredSessionToken();
      if (!token) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const me = await fetchAuthMe(token);
        if (!mounted) return;
        setSessionToken(token);
        setUser(me);
      } catch {
        setStoredSessionToken(null);
        if (!mounted) return;
        setSessionToken(null);
        setUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const applySession = useCallback(async (identityToken: string) => {
    setDeniedMessage(null);
    const { token, user: nextUser } = await exchangeAppleIdentityToken(identityToken);
    setStoredSessionToken(token);
    setSessionToken(token);
    setUser(nextUser);
  }, []);

  const signInWithApple = useCallback(async () => {
    if (!isAppleClientConfigured()) {
      throw new Error("Missing VITE_APPLE_CLIENT_ID.");
    }
    await loadAppleScript();
    if (!window.AppleID) throw new Error("Apple Sign In JS failed to load.");

    window.AppleID.auth.init({
      clientId: appleClientId(),
      scope: "name email",
      redirectURI: appleRedirectUri(),
      usePopup: true,
    });

    let identityToken: string | undefined;
    try {
      const result = await window.AppleID.auth.signIn();
      identityToken = result.authorization?.id_token;
    } catch (err) {
      throw describeAppleError(err);
    }
    if (!identityToken) throw new Error("Apple did not return an identity token.");
    await applySession(identityToken);
  }, [applySession]);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      configured: isAppleClientConfigured(),
      sessionToken,
      user,
      isAdmin: Boolean(sessionToken && user),
      deniedMessage,
      signInWithApple,
      completeAppleRedirect: applySession,
      signOut: async () => {
        setStoredSessionToken(null);
        setSessionToken(null);
        setUser(null);
        setDeniedMessage(null);
      },
    }),
    [loading, sessionToken, user, deniedMessage, signInWithApple, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
